import config from "lib/fr-config";
import { Cookies } from "react-cookie";
import { getAge } from "lib/commonUtil";
import { sendError } from "lib/sentryHelper";

const FRONTEND_VERSION = config.version;
const { TAG_ID } = config.googleAnalytics;

const _getUsersAge = birthday => {
  const year = Number(birthday.substr(0, 4));
  const month = Number(birthday.substr(4, 2));
  const day = Number(birthday.substr(6, 2));

  return getAge(year, month, day);
};

const _getRange = (age, lengthOfRange) => {
  const START_AGE = 14;

  const ageOffset = Number(age) - START_AGE;

  if (ageOffset < 0) return `under${START_AGE}`;

  const ageMod = ageOffset % lengthOfRange;
  const startNum = Number(age) - ageMod;
  return `${startNum}-${startNum + lengthOfRange - 1}`;
};

const GaHelper = (function () {
  let gtag = null;
  let alreadyConfigured = false;
  let hasUserDeniedAnalysis = true;

  const setGtag = () => {
    if (gtag) return true;

    gtag = window.gtag;
    if (!gtag) {
      return false;
    } else {
      return true;
    }
  };

  const isLocalhost = () => {
    return window.location.href.includes("localhost");
  };

  const checkUserConsentForAnalysis = () => {
    const cookies = new Cookies();
    const rawCookieValue = cookies.get("MaConsent");
    const cookieValueObj =
      typeof rawCookieValue === "string"
        ? JSON.parse(rawCookieValue)
        : rawCookieValue;

    if (!cookieValueObj) {
      hasUserDeniedAnalysis = true;
      return null;
    }

    if (typeof cookieValueObj.analyticsCookie !== "boolean") {
      const errObj = Error("analyticsCookie not found in cookie object.");
      errObj.errorValue = { ...cookieValueObj };
      sendError(errObj);

      hasUserDeniedAnalysis = true;
      return null;
    } else {
      hasUserDeniedAnalysis = !cookieValueObj.analyticsCookie;
      return null;
    }
  };

  return {
    initGAUserInfo: function ({ gaUserId, location, gender, birthday }) {
      if (isLocalhost()) return false;

      checkUserConsentForAnalysis();
      if (hasUserDeniedAnalysis) return false;

      if (!setGtag()) {
        const errObj = Error("gtag is null");
        errObj.errorValue = { gaUserId };
        sendError(errObj);
        return false;
      }

      switch (alreadyConfigured) {
        case true:
          gtag("set", "user_id", gaUserId);
          break;

        case false:
        default:
          gtag("config", TAG_ID, { user_id: gaUserId });

          alreadyConfigured = true;
          break;
      }

      const userPropObj = { web_frontend_version: FRONTEND_VERSION };
      if (gaUserId) {
        const usersAge = !!birthday ? _getUsersAge(birthday) : "nullAge";
        const ageGroup4 = !!birthday ? _getRange(usersAge, 4) : "nullGroup";

        userPropObj["location_set_by_user"] = location;
        userPropObj["gender_set_by_user"] = gender;
        userPropObj["age_set_by_user"] = usersAge;
        userPropObj["age_group_4_set_by_user"] = ageGroup4;
      } else {
        userPropObj["location_set_by_user"] = "guest";
        userPropObj["gender_set_by_user"] = "guest";
        userPropObj["age_set_by_user"] = "guest";
        userPropObj["age_group_4_set_by_user"] = "guest";
      }
      gtag("set", "user_properties", userPropObj);
      return true;
    },
    setGAEvent: function ({ type }) {
      if (isLocalhost()) return false;

      if (hasUserDeniedAnalysis) return false;

      if (!setGtag()) return false;

      switch (type) {
        case "ma_view_sign_up_form_1st":
        case "ma_view_sign_up_form_2nd":
        case "ma_sign_up":
        case "ma_view_membership_start_btn":
        case "ma_subscribe_membership":
        case "ma_start_free_trial":
          gtag("event", type);
          return true;

        default:
          const errObj = Error("wrong ga event type");
          errObj.errorValue = { type };
          sendError(errObj);
          return false;
      }
    },
    setGAClickEvent: function ({ type, loc }) {
      if (isLocalhost()) return false;

      if (hasUserDeniedAnalysis) return false;

      if (!setGtag()) return false;

      switch (type) {
        case "membershipRecommendationBtn":
        case "logInRecommendationBtn":
        case "membershipStartBtn":
          break;

        default:
          const errObj = Error("wrong btnType of ga click event");
          errObj.errorValue = { type };
          sendError(errObj);
          return false;
      }

      switch (loc) {
        case null:
        case undefined:
        case "loungePage-lower":
        case "outfitPage-item":
        case "profileSection":
          break;

        default:
          const errObj = Error("wrong btnLocation of ga click event");
          errObj.errorValue = { loc };
          sendError(errObj);
          return false;
      }

      const option = { button_type: type };
      if (loc) option["button_location"] = loc;
      gtag("event", "ma_click", option);
      return true;
    },
    setGASearchEvent: function (queryObj) {
      if (isLocalhost()) return false;

      if (hasUserDeniedAnalysis) return false;

      if (!setGtag()) return false;

      const { category, brand, color, tag, gender } = queryObj;

      if (!category || !gender) {
        const errObj = Error("null qry of ga search event");
        errObj.errorValue = { category, gender };
        sendError(errObj);
        return false;
      }

      const option = {
        search_qry_category: category,
        search_qry_gender: gender
      };
      if (brand) option["search_qry_brand"] = brand;
      if (color) option["search_qry_color"] = color;
      if (tag) option["search_qry_tag"] = tag;
      gtag("event", "ma_search", option);
      return true;
    },
    setGARequestOutfitListEvent: function ({ type, pageNum, detailObj }) {
      if (isLocalhost()) return false;

      if (hasUserDeniedAnalysis) return false;

      if (!setGtag()) return false;

      let option = {};
      switch (type) {
        case "searchOutfitList":
          if (!detailObj.category || !detailObj.gender) {
            const errObj = Error(
              "wrong detailObj of ga request outfit list event"
            );
            errObj.errorValue = { ...detailObj };
            sendError(errObj);
            return false;
          } else {
            option["search_qry_category"] = detailObj.category;
            option["search_qry_gender"] = detailObj.gender;
            if (detailObj.brand) option["search_qry_brand"] = detailObj.brand;
            if (detailObj.color) option["search_qry_color"] = detailObj.color;
            if (detailObj.tag) option["search_qry_tag"] = detailObj.tag;
          }
          break;
        case "followingsOutfitList":
        case "galleryOutfitList":
        case "myOutfitList":
        case "othersOutfitList":
        case "bookmarkOutfitList":
        case "pickOutfitList":
        case "outfitWithItemList":
          break;

        default:
          const errObj = Error("wrong type of ga request outfit list event");
          errObj.errorValue = { type };
          sendError(errObj);
          return false;
      }

      if (typeof pageNum !== "number") {
        const errObj = Error("wrong pageNum of ga request outfit list event");
        errObj.errorValue = { pageNum };
        sendError(errObj);
        return false;
      }

      if (pageNum === 1) return true;

      option["requested_outfit_list_type"] = type;
      option["requested_outfit_list_page_num_12"] = pageNum;
      gtag("event", "ma_request_outfit_list", option);
      return true;
    }
  };
})();

export default GaHelper;
